import BasketSvg from "@/assets/svg/BasketSvg";
import EditPostSvg from "@/assets/svg/EditPostSvg";
import ReportPostSvg from "@/assets/svg/ReportPostSvg";
import Popup from "@/components/UI/popup/Popup";
import {
  CardContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { FC, MouseEvent, useState } from "react";
import { formatTimeAgo } from "@/helpers/getHoursDifference";
import { styles } from "../stylesHome";
import { IPost } from "@/store/api/postApiData";
import { UserType } from "@/store/api/authApiData";
import { useNavigate } from "react-router-dom";
import CreatePostModal from "@/components/UI/modal/CreatePostModal/CreatePostModal";
import { RequireAuth } from "@/components/RequireAuth";
import CreateLongPostModal from "@/components/UI/modal/CreateLongPostModal/CreateLongPostModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import MoreSvg from "@/assets/svg/MoreSvg";
import PremiumCheckmark from "@/assets/svg/PremiumCheckmark";
import { Box } from "@mui/material";
import { useAppSelector } from "@/hooks/useAppSelector";

interface IPostHeaderProps {
  post: IPost;
  user: UserType | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  handleClick: (event: MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  handleDelete: () => void;
  handleReport: (uid: string) => void;
}

const PostHeader: FC<IPostHeaderProps> = ({
  post,
  user,
  anchorEl,
  open,
  handleClick,
  handleClose,
  handleDelete,
  handleReport,
}) => {
  const [editPostModal, setEditPostModal] = useState(false);
  const [editLongPostModal, setEditLongPostModal] = useState(false);
  const [editPost, setEditPost] = useState<Record<string, any>>({});
  const navigate = useNavigate();

  const author = post?.author ?? null;
  const authorUid = author?.uid ?? null;
  const authorUsername = author?.username ?? "";
  const longPost = post?.isLongForm;
  const [toast, setToast] = useState<{
    message: string;
    success: boolean;
    link?: { text: string; href: string };
  } | null>(null);
  const goToProfile = () => {
    if (authorUid) navigate(`/profile/${authorUid}`);
  };

  const isOwner = !!authorUid && !!user?.uid && authorUid === user.uid;
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const showPremiumCheckmark = author?.isPremium || (isOwner && subscriptionStatus === "ACTIVE");

  return (
    <CardContent sx={{ p: 0 }}>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
          link={toast.link}
        />
      )}
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
        position="relative"
        sx={{ height: "24px", marginBottom: "5px", display: "flex", alignItems: "center" }}
      >
        <Stack direction="row" alignItems={"center"} position="relative">
          <Popup type="user" uid={authorUid || undefined}>
            <Stack
              direction="row"
              alignItems="center"
              gap={"4px"}
              onClick={goToProfile}
              sx={{ cursor: "pointer" }}
            >
              {authorUsername && (
                <Typography
                  variant="h5"
                  sx={{
                    color: "#3C3A3A",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: "15px",
                    lineHeight: 1,
                    letterSpacing: "0.02em",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {authorUsername}
                  {showPremiumCheckmark && (
                    <Box sx={{ width: "20px", height: "20px", marginLeft: "0" }}>
                      <PremiumCheckmark />
                    </Box>
                  )}
                </Typography>
              )}

              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#9DA6B0",
                }}
              >
                {formatTimeAgo(post.createdAt)}
              </Typography>
            </Stack>
          </Popup>
        </Stack>

        <IconButton
          size="small"
          aria-label="more options"
          aria-controls={open ? "post-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          sx={{ padding: 0 }}
        >
          <MoreSvg variant="outlined" />
        </IconButton>

        <Menu
          id="post-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "post-menu-button",
            sx: {
              paddingTop: 0,
              paddingBottom: 0,
            },
          }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          PaperProps={styles.postSelectDropdownMenu}
        >
          {isOwner
            ? [
                <MenuItem
                  key={"edit"}
                  onClick={() => {
                    if (post.isLongForm) {
                      setEditPost({
                        ...post,
                        open: true,
                      });
                      setEditLongPostModal(true);
                      handleClose();
                    } else {
                      setEditPost({
                        ...post,
                        open: true,
                      });
                      setEditPostModal(true);
                      handleClose();
                    }
                  }}
                  sx={{
                    padding: "1rem 2.3rem 0.5rem 1.3314rem",
                    gap: "0.25rem",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "0px !important" }}>
                    <EditPostSvg height={16} width={16} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography sx={styles.menuOptionText}>
                      {longPost ? "Edit Article" : "Edit"}
                    </Typography>
                  </ListItemText>
                </MenuItem>,
                <MenuItem
                  key={"delete"}
                  onClick={() => {
                    handleDelete();
                    handleClose();
                  }}
                  sx={{
                    padding: "0.5rem 2.3rem 1rem 1.3314rem",
                    gap: "0.25rem",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "0px !important" }}>
                    <BasketSvg style={{ width: "1rem", height: "1rem" }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography color="error" sx={styles.menuOptionText}>
                      {longPost ? "Delete Article" : "Delete post"}
                    </Typography>
                  </ListItemText>
                </MenuItem>,
              ]
            : [
                <RequireAuth key={"report"}>
                  <MenuItem
                    onClick={() => {
                      handleReport(post.uid);
                      handleClose();
                    }}
                    sx={{ gap: "0.25rem", padding: "1rem 1.3314rem" }}
                    key={"report"}
                  >
                    <ListItemIcon sx={{ minWidth: "0px !important" }}>
                      <ReportPostSvg />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography sx={{ ...styles.menuOptionText, color: "#CE212B" }}>
                        Report a post
                      </Typography>
                    </ListItemText>
                  </MenuItem>
                </RequireAuth>,
              ]}
        </Menu>
      </Stack>
      <>
        {editPostModal && (
          <CreatePostModal
            open={editPostModal}
            handleClose={() => {
              setEditPostModal(false);
              setEditPost({
                ...editPost,
                open: false,
              });
            }}
            editPost={editPost}
            postVariant={
              editPost?.topic
                ? editPost.topic.charAt(0).toUpperCase() + editPost.topic.slice(1).toLowerCase()
                : undefined
            }
            setToast={setToast}
          />
        )}
      </>
      <>
        {editLongPostModal && (
          <CreateLongPostModal
            open={editLongPostModal}
            handleClose={() => {
              setEditLongPostModal(false);
              setEditPost({
                ...editPost,
                open: false,
              });
            }}
            editPost={editPost}
            setToast={setToast}
            setIsLongPostModalOpen={setEditLongPostModal}
          />
        )}
      </>
    </CardContent>
  );
};

export default PostHeader;
